import { Component } from '@theme/component';
import {
  isValidEmail,
  subscribeToList,
  parseConfig,
  getSelectedLists,
  syncGenderField as applyGenderVisibility,
  getGenderProperty,
} from '@theme/klaviyo-newsletter-api';

/**
 * Klaviyo newsletter sign-up component.
 *
 * @typedef {object} KlaviyoNewsletterSignUpRefs
 * @property {HTMLElement} formContainer - The form container element.
 * @property {HTMLInputElement} emailInput - The email input.
 * @property {HTMLInputElement} [listACheckbox] - The first list checkbox.
 * @property {HTMLInputElement} [listBCheckbox] - The second list checkbox.
 * @property {HTMLInputElement} [disclaimerCheckbox] - The disclaimer checkbox.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} submitText - The submit button text.
 * @property {HTMLElement} loadingIndicator - The loading indicator.
 * @property {HTMLElement} successMessage - The success message container.
 * @property {HTMLElement} errorMessage - The error message element.
 *
 * @extends Component<KlaviyoNewsletterSignUpRefs>
 */
class KlaviyoNewsletterSignUp extends Component {
  requiredRefs = ['formContainer', 'emailInput', 'submitButton', 'errorMessage'];

  #config = null;

  connectedCallback() {
    super.connectedCallback();
    this.#config = parseConfig(this);
    this.#prefillEmail();
    // Store customization: set initial gender-field visibility from the American Eagle toggle
    this.syncGenderField();
  }

  // Store customization: show/hide gender field on brand change (public for on:change="/syncGenderField")
  syncGenderField = () => applyGenderVisibility(this.refs);

  /**
   * Gets the Klaviyo Company ID from the config.
   * @returns {string}
   */
  get companyId() {
    return this.#config.companyId || '';
  }

  /**
   * Gets the first list ID from the config.
   * @returns {string}
   */
  get listAId() {
    return this.#config.listAId || '';
  }

  /**
   * Gets the second list ID from the config.
   * @returns {string}
   */
  get listBId() {
    return this.#config.listBId || '';
  }

  /**
   * Gets the locale data for Klaviyo profile properties.
   * @returns {{ locale: string, language: string, country: string, market: string } | null}
   */
  get localeData() {
    if (!this.#config.locale) return null;
    return {
      locale: this.#config.locale,
      language: this.#config.localeLanguage || '',
      country: this.#config.localeCountry || '',
      market: this.#config.market || '',
    };
  }

  /**
   * Gets the logged-in customer's email.
   * @returns {string|null}
   */
  get customerEmail() {
    return this.#config.customerEmail || null;
  }

  /**
   * Gets the email error message from the config.
   * @returns {string}
   */
  get errorEmailMessage() {
    return this.#config.errors?.email || 'Please enter a valid email address';
  }

  /**
   * Gets the list selection error message from the config.
   * @returns {string}
   */
  get errorListMessage() {
    return this.#config.errors?.list || 'Please select at least one email list';
  }

  /**
   * Gets the API error message from the config.
   * @returns {string}
   */
  get errorApiMessage() {
    return this.#config.errors?.api || 'Something went wrong. Please try again.';
  }

  /**
   * Gets the disclaimer error message from the config.
   * @returns {string}
   */
  get errorDisclaimerMessage() {
    return this.#config.errors?.disclaimer || 'Please accept the disclaimer to continue';
  }

  /**
   * Prefills the email input for logged-in customers.
   */
  #prefillEmail() {
    if (this.customerEmail) {
      this.refs.emailInput.value = this.customerEmail;
    }
  }

  /**
   * Handles the form submission.
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event?.preventDefault();

    const { emailInput, listACheckbox, listBCheckbox, submitButton, loadingIndicator, submitText } = this.refs;

    // Hide any previous errors
    this.#hideError();

    // Validate email
    if (!emailInput.value || !isValidEmail(emailInput.value)) {
      this.#showError(this.errorEmailMessage);
      emailInput.focus();
      return;
    }

    // Get selected lists
    const selectedLists = getSelectedLists(this.refs, this.listAId, this.listBId);

    // If multiple lists are available, at least one must be selected
    if ((listACheckbox || listBCheckbox) && selectedLists.length === 0) {
      this.#showError(this.errorListMessage);
      return;
    }

    // Validate disclaimer checkbox if present
    if (this.refs.disclaimerCheckbox && !this.refs.disclaimerCheckbox.checked) {
      this.#showError(this.errorDisclaimerMessage);
      return;
    }

    // Show loading state
    submitButton.disabled = true;
    submitText.hidden = true;
    loadingIndicator.hidden = false;

    try {
      // Subscribe to each selected list
      const listsToSubscribe = selectedLists.length > 0 ? selectedLists : this.listAId ? [this.listAId] : [];

      // Store customization: gender is sent only when the American Eagle list is selected
      const customProperties = getGenderProperty(this.refs);

      await Promise.all(
        listsToSubscribe.map((listId) =>
          subscribeToList(emailInput.value, listId, this.companyId, this.localeData, customProperties),
        ),
      );

      this.#showSuccess();
    } catch (error) {
      console.error('Klaviyo newsletter subscription failed:', error);
      this.#showError(this.errorApiMessage);
    } finally {
      submitButton.disabled = false;
      submitText.hidden = false;
      loadingIndicator.hidden = true;
    }
  };

  /**
   * Shows an error message.
   * @param {string} message - The error message to display.
   */
  #showError(message) {
    const { errorMessage, emailInput } = this.refs;
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    }
    if (emailInput && errorMessage?.id) {
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.setAttribute('aria-describedby', errorMessage.id);
    }
  }

  /**
   * Hides the error message.
   */
  #hideError() {
    const { errorMessage, emailInput } = this.refs;
    if (errorMessage) {
      errorMessage.hidden = true;
      errorMessage.textContent = '';
    }
    if (emailInput) {
      emailInput.removeAttribute('aria-invalid');
      emailInput.removeAttribute('aria-describedby');
    }
  }

  /**
   * Shows the success state.
   */
  #showSuccess() {
    const { formContainer, successMessage } = this.refs;
    if (formContainer) formContainer.hidden = true;
    if (successMessage) successMessage.hidden = false;
  }
}

if (!customElements.get('klaviyo-newsletter-sign-up')) {
  customElements.define('klaviyo-newsletter-sign-up', KlaviyoNewsletterSignUp);
}

export { KlaviyoNewsletterSignUp };
